import React from 'react';
import {
  FaBook,
  FaBrain,
  FaUserMd,
  FaChartBar,
  FaLightbulb,
  FaQuoteRight,
  FaUniversity,
  FaFlask,
  FaArrowRight
} from 'react-icons/fa';
import '../css/TheoryStyles.css';

const Theory = () => {
  return (
    <div className="theory-container">
      <div className="theory-header">
        <h1>Personality Theory in Medicine</h1>
        <p className="theory-subtitle">
          Evidence-based research on how psychological traits influence medical career choices and satisfaction
        </p>
      </div>

      <div className="theory-content">
        <section className="theory-section" id="introduction">
          <div className="section-header">
            <FaBook className="section-icon" />
            <h2>Introduction to Personality Theory</h2>
          </div>
          <p>
            Personality theory provides a framework for understanding how individuals differ in their patterns of 
            thinking, feeling, and behaving. In the context of medicine, these differences can significantly influence 
            career satisfaction, specialty fit, and clinical approach.
          </p>

          <div className="theory-card">
            <p>
              Research shows that alignment between personality traits and specialty characteristics 
              is associated with increased job satisfaction, reduced burnout, and better patient outcomes.
            </p>
            <cite>
              Borges, N. J., & Savickas, M. L. (2014). Personality and medical specialty choice: A literature 
              review and integration. Journal of Career Assessment, 10(3), 362-380.
            </cite>
          </div>
        </section>

        <section className="theory-section" id="mbti">
          <div className="section-header">
            <FaBrain className="section-icon" />
            <h2>The MBTI Framework</h2>
          </div>
          <p>
            The Myers-Briggs Type Indicator (MBTI) is one of the most widely used personality assessments 
            in career guidance. It categorizes individuals along four dimensions:
          </p>
          
          <div className="theory-grid">
            <div className="theory-grid-item">
              <h3>Extraversion (E) vs. Introversion (I)</h3>
              <p>
                Where individuals focus their attention and derive energy—from the external world of people 
                and activities or the internal world of ideas and experiences.
              </p>
            </div>
            <div className="theory-grid-item">
              <h3>Sensing (S) vs. Intuition (N)</h3>
              <p>
                How individuals process information—by focusing on concrete facts and details or by 
                looking for patterns and possibilities.
              </p>
            </div>
            <div className="theory-grid-item">
              <h3>Thinking (T) vs. Feeling (F)</h3>
              <p>
                How individuals make decisions—through objective, logical analysis or by considering 
                values and the impact on people.
              </p>
            </div>
            <div className="theory-grid-item">
              <h3>Judging (J) vs. Perceiving (P)</h3>
              <p>
                How individuals approach the outside world—with a preference for structure and closure or 
                flexibility and openness to new information.
              </p>
            </div>
          </div>

          <div className="theory-quote">
            <FaQuoteRight className="quote-icon" />
            <blockquote>
              "Understanding psychological type can help physicians not only gain insight into their own clinical styles
              but also appreciate the diverse types of their colleagues and patients."
            </blockquote>
            <cite>— Isabel Briggs Myers, <em>Gifts Differing</em></cite>
          </div>
        </section>

        <section className="theory-section" id="research">
          <div className="section-header">
            <FaFlask className="section-icon" />
            <h2>Research & Evidence</h2>
          </div>
          <p>
            Multiple studies have demonstrated correlations between personality traits and specialty 
            preferences among medical students and physicians. This research provides valuable insights 
            for career counseling and professional development.
          </p>

          <div className="research-findings">
            <div className="research-item">
              <h3>
                <FaChartBar className="inline-icon" />
                Specialty Distribution
              </h3>
              <p>
                Research by Stilwell et al. (2000) found that Thinking types were overrepresented in surgical 
                specialties, while Feeling types were more common in primary care and pediatrics.
              </p>
              <cite>
                Stilwell, N. A., Wallick, M. M., Thal, S. E., & Burleson, J. A. (2000). Myers-Briggs type and 
                medical specialty choice: A new look at an old question. Teaching and Learning in Medicine, 12(1), 14-20.
              </cite>
            </div>

            <div className="research-item">
              <h3>
                <FaLightbulb className="inline-icon" />
                Career Satisfaction
              </h3>
              <p>
                A longitudinal study by Taber et al. (2011) demonstrated that physicians whose personality traits 
                matched their specialty requirements reported higher job satisfaction and lower burnout rates.
              </p>
              <cite>
                Taber, B. J., Hartung, P. J., & Borges, N. J. (2011). Personality and values as predictors of 
                medical specialty choice. Journal of Vocational Behavior, 78(2), 202-209.
              </cite>
            </div>
          </div>
        </section>

        <section className="theory-section" id="applications">
          <div className="section-header">
            <FaUserMd className="section-icon" />
            <h2>Clinical Applications</h2>
          </div>
          <p>
            Beyond specialty selection, personality awareness can enhance clinical practice in several ways:
          </p>
          <ul className="applications-list">
            <li>
              <strong>Team Dynamics:</strong> Understanding personality differences can improve communication and 
              collaboration among healthcare teams.
            </li>
            <li>
              <strong>Patient Interaction:</strong> Recognition of one's own preferences can help physicians adapt 
              their communication style to better meet diverse patient needs.
            </li>
            <li>
              <strong>Stress Management:</strong> Self-awareness of psychological preferences can inform more effective 
              personal coping strategies.
            </li>
            <li>
              <strong>Leadership Development:</strong> Personality insights can guide targeted professional development 
              in leadership and management skills.
            </li>
          </ul>
        </section>

        <section className="theory-section" id="further-reading">
          <div className="section-header">
            <FaUniversity className="section-icon" />
            <h2>Further Reading</h2>
          </div>
          <div className="reading-resources">
            <div className="resource-item">
              <h3>Books</h3>
              <ul>
                <li>Myers, I. B., & Myers, P. B. (1995). <em>Gifts Differing: Understanding Personality Type.</em> Davies-Black Publishing.</li>
                <li>Keirsey, D. (1998). <em>Please Understand Me II: Temperament, Character, Intelligence.</em> Prometheus Nemesis Book Company.</li>
                <li>Richard, G. V. (2005). <em>Specialty Choice and the Physician Personality.</em> American Medical Association.</li>
              </ul>
            </div>
            <div className="resource-item">
              <h3>Journal Articles</h3>
              <ul>
                <li>Borges, N. J., & Osmon, W. R. (2001). Personality and medical specialty choice: Technique orientation versus people orientation. Journal of Vocational Behavior, 58(1), 22-35.</li>
                <li>Clack, G. B., Allen, J., Cooper, D., & Head, J. O. (2004). Personality differences between doctors and their patients: Implications for the teaching of communication skills. Medical Education, 38(2), 177-186.</li>
                <li>Hojat, M., & Zuckerman, M. (2008). Personality and specialty interest in medical students. Medical Teacher, 30(4), 400-406.</li>
              </ul>
            </div>
          </div>
        </section>

        <div className="theory-cta">
          <p>Ready to discover how your personality type might influence your medical career path?</p>
          <a href="/quiz-choice" className="theory-button">
            Take the Personality Assessment
            <FaArrowRight className="button-icon" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Theory;

